import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Search,
  CheckCircle,
  AlertTriangle,
  AlertOctagon,
  Info,
  Clock,
  Calendar,
  X,
  FileSpreadsheet,
  Edit2,
  Trash2,
  Loader2,
  UserCheck,
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Input, Button, Modal } from '../../Components/ui';
import { useApi } from '../../api/useApi';

const hojeISO = () => new Date().toISOString().split('T')[0];

const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export default function FeedbackDay() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const api = useApi();
  const queryClient = useQueryClient();

  const [dataEvento, setDataEvento] = useState(hojeISO());
  const [buscaAluno, setBuscaAluno] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('todos'); // 'todos', '1', '2', '3', '4', etc.
  const [filtroStatus, setFiltroStatus] = useState('todos'); // 'todos', 'pendentes', 'presentes'

  const handleMudarBusca = (e) => {
    setBuscaAluno(e.target.value);
  };

  // Modal de Check-in do Aluno Selecionado
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [modalCheckinAberto, setModalCheckinAberto] = useState(false);

  // Modal Lista de Presentes
  const [modalPresentesAberto, setModalPresentesAberto] = useState(false);
  const [filtroPresentes, setFiltroPresentes] = useState('todos'); // 'todos', 'urgentes', 'aguardando', 'atendidos'
  const [buscaPresentes, setBuscaPresentes] = useState('');

  // Form states do Check-in
  const [responsavelPresente, setResponsavelPresente] = useState('');
  const [parentesco, setParentesco] = useState('');
  const [observacao, setObservacao] = useState('');
  const [verDetalhesSensor, setVerDetalhesSensor] = useState(false);

  // Modal Prioridade Manual
  const [modalPrioAberto, setModalPrioAberto] = useState(false);
  const [prioManualNivel, setPrioManualNivel] = useState('vermelho');
  const [prioManualMotivo, setPrioManualMotivo] = useState('');

  // Query: Lista de Alunos e Presenças
  const { data: alunos = [], isLoading: carregandoAlunos } = useQuery({
    queryKey: ['feedback-day-alunos', institutionId, dataEvento],
    queryFn: async () => {
      const res = await api.get(`/api/feedback-day/alunos?data_evento=${dataEvento}`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token
  });

  // Query: Resumo do Evento
  const { data: resumo = {} } = useQuery({
    queryKey: ['feedback-day-resumo', institutionId, dataEvento],
    queryFn: async () => {
      const res = await api.get(`/api/feedback-day/resumo?data_evento=${dataEvento}`);
      return res?.data ?? res ?? {};
    },
    enabled: !!institutionId && !!token
  });

  // Mutação: Registrar Presença
  const mutacaoPresenca = useMutation({
    mutationFn: async (dados) => {
      return await api.post('/api/feedback-day/presenca', dados);
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Presença registrada com sucesso!');
      queryClient.invalidateQueries(['feedback-day-alunos']);
      queryClient.invalidateQueries(['feedback-day-resumo']);
      setModalCheckinAberto(false);
      setAlunoSelecionado(null);
      setResponsavelPresente('');
      setParentesco('');
      setObservacao('');
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao registrar presença.');
    }
  });

  // Mutação: Remover Presença
  const mutacaoRemover = useMutation({
    mutationFn: async (presencaId) => {
      return await api.delete(`/api/feedback-day/presenca/${presencaId}`);
    },
    onSuccess: () => {
      toast.success('Presença desfeita com sucesso.');
      queryClient.invalidateQueries(['feedback-day-alunos']);
      queryClient.invalidateQueries(['feedback-day-resumo']);
      setModalCheckinAberto(false);
      setAlunoSelecionado(null);
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao remover presença.');
    }
  });

  // Mutação: Alternar Atendido
  const mutacaoAtendido = useMutation({
    mutationFn: async ({ presencaId, atendido }) => {
      return await api.patch(`/api/feedback-day/presenca/${presencaId}/atendido`, { atendido });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['feedback-day-alunos']);
      queryClient.invalidateQueries(['feedback-day-resumo']);
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao atualizar atendimento.');
    }
  });

  // Mutação: Definir Prioridade Manual
  const mutacaoPrioridade = useMutation({
    mutationFn: async (dados) => {
      return await api.post('/api/feedback-day/prioridade', dados);
    },
    onSuccess: () => {
      toast.success('Prioridade do aluno ajustada!');
      queryClient.invalidateQueries(['feedback-day-alunos']);
      setModalPrioAberto(false);
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao definir prioridade.');
    }
  });

  // Lista dos alunos presentes
  const presentes = useMemo(() => {
    return alunos.filter((a) => a.presente);
  }, [alunos]);

  // Lista de níveis disponíveis extraída dos alunos
  const niveisDisponiveis = useMemo(() => {
    const mapa = new Map();
    alunos.forEach((a) => {
      let chave = 'outros';
      let label = 'Outros';

      if (a.nivelNumero !== null && a.nivelNumero !== undefined) {
        chave = String(a.nivelNumero);
        label = `Nível ${a.nivelNumero}`;
      } else if (a.nivel && String(a.nivel).toLowerCase().includes('nível')) {
        chave = String(a.nivel).replace(/[^0-9]/g, '') || 'outros';
        label = a.nivel;
      } else if (a.turma) {
        chave = a.turma;
        label = `Turma ${a.turma}`;
      }

      if (!mapa.has(chave)) {
        mapa.set(chave, { chave, label, total: 0 });
      }
      mapa.get(chave).total += 1;
    });

    return Array.from(mapa.values()).sort((a, b) => {
      const numA = parseInt(a.chave, 10);
      const numB = parseInt(b.chave, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.label.localeCompare(b.label);
    });
  }, [alunos]);

  // Filtragem da Lista Principal de Alunos
  const alunosFiltrados = useMemo(() => {
    let lista = alunos;

    // Filtro por Nível
    if (filtroNivel !== 'todos') {
      lista = lista.filter((a) => {
        if (filtroNivel === 'outros') {
          return !a.nivelNumero && (!a.turma || a.turma === '');
        }
        if (a.nivelNumero !== null && a.nivelNumero !== undefined) {
          return String(a.nivelNumero) === filtroNivel;
        }
        if (a.turma) {
          return a.turma === filtroNivel;
        }
        return false;
      });
    }

    // Filtro por Status (Pendentes ou Presentes)
    if (filtroStatus === 'pendentes') {
      lista = lista.filter((a) => !a.presente);
    } else if (filtroStatus === 'presentes') {
      lista = lista.filter((a) => a.presente);
    }

    // Filtro por Busca de Texto
    if (buscaAluno.trim()) {
      const termo = normalizar(buscaAluno);
      lista = lista.filter((a) => {
        return (
          normalizar(a.nome).includes(termo) ||
          normalizar(a.responsavel_nome).includes(termo) ||
          normalizar(a.turma).includes(termo) ||
          normalizar(a.presenca?.responsavel_presente).includes(termo)
        );
      });
    }

    return lista;
  }, [alunos, filtroNivel, filtroStatus, buscaAluno]);

  // Filtragem dos presentes para o Modal de Presentes
  const presentesFiltrados = useMemo(() => {
    let lista = presentes;

    if (filtroPresentes === 'urgentes') {
      lista = lista.filter(
        (a) => a.sensorPrioridade?.nivel === 'vermelho' || a.sensorPrioridade?.nivel === 'laranja'
      );
    } else if (filtroPresentes === 'aguardando') {
      lista = lista.filter((a) => !a.presenca?.atendido);
    } else if (filtroPresentes === 'atendidos') {
      lista = lista.filter((a) => a.presenca?.atendido);
    }

    if (buscaPresentes.trim()) {
      const termo = normalizar(buscaPresentes);
      lista = lista.filter(
        (a) =>
          normalizar(a.nome).includes(termo) ||
          normalizar(a.presenca?.responsavel_presente).includes(termo) ||
          normalizar(a.turma).includes(termo)
      );
    }

    return lista;
  }, [presentes, filtroPresentes, buscaPresentes]);

  // Ao selecionar um aluno (abre o popup de check-in)
  const handleAbrirCheckin = (aluno) => {
    setAlunoSelecionado(aluno);
    setVerDetalhesSensor(false);

    if (aluno.presente) {
      setResponsavelPresente(aluno.presenca?.responsavel_presente || '');
      setParentesco(aluno.presenca?.parentesco || '');
      setObservacao(aluno.presenca?.observacao || '');
    } else {
      setResponsavelPresente(aluno.responsavel_nome || '');
      setParentesco(aluno.responsavel_nome ? 'Responsável' : '');
      setObservacao('');
    }
    setModalCheckinAberto(true);
  };

  // Submeter presença
  const handleSalvarPresenca = (e) => {
    e.preventDefault();
    if (!alunoSelecionado) return;
    if (!responsavelPresente.trim()) {
      toast.warning('Informe o nome do responsável presente.');
      return;
    }

    mutacaoPresenca.mutate({
      id_aluno: alunoSelecionado.id,
      data_evento: dataEvento,
      responsavel_presente: responsavelPresente,
      parentesco,
      observacao,
      prioridade_atendimento: alunoSelecionado.sensorPrioridade?.nivel || 'verde'
    });
  };

  // Confirmar exclusão de presença
  const handleConfirmarRemocao = async (presencaId, nomeAluno) => {
    const ok = await confirm({
      title: 'Desfazer presença?',
      message: `Tem certeza que deseja remover o check-in de ${nomeAluno}?`,
      confirmLabel: 'Sim, remover',
      danger: true
    });
    if (ok) {
      mutacaoRemover.mutate(presencaId);
    }
  };

  // Exportar lista para CSV
  const exportarCSV = () => {
    if (presentes.length === 0) {
      toast.warning('Nenhum participante registrado para exportar.');
      return;
    }

    const colunas = [
      'Horário',
      'Aluno',
      'Nível/Turma',
      'Turno',
      'Prioridade Sensor',
      'Professor que Apontou',
      'Motivo Prioridade',
      'Responsável Presente',
      'Parentesco',
      'Atendido pela Coordenação',
      'Observação'
    ];

    const linhas = presentes.map((a) => {
      const p = a.presenca || {};
      const s = a.sensorPrioridade || {};
      const hora = p.presenca_horario
        ? new Date(p.presenca_horario).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '';
      return [
        hora,
        `"${a.nome || ''}"`,
        `"${a.nivel || a.turma || ''}"`,
        `"${a.turno || ''}"`,
        `"${s.label || ''}"`,
        `"${s.professorNome || ''}"`,
        `"${(s.motivo || '').replace(/"/g, '""')}"`,
        `"${p.responsavel_presente || ''}"`,
        `"${p.parentesco || ''}"`,
        p.atendido ? 'Sim' : 'Não',
        `"${(p.observacao || '').replace(/"/g, '""')}"`
      ].join(';');
    });

    const csvContent = '\uFEFF' + [colunas.join(';'), ...linhas].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `feedback_day_presencas_${dataEvento}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Lista exportada com sucesso!');
  };

  // Render do Badge do Sensor de Prioridade
  const renderSensorBadge = (sensor, compact = false) => {
    if (!sensor) return null;

    let bgClass = 'bg-emerald-50 text-emerald-700 border-emerald-300';
    let icon = <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />;

    if (sensor.nivel === 'vermelho') {
      bgClass = 'bg-rose-50 text-rose-800 border-rose-300';
      icon = <AlertOctagon className="w-3.5 h-3.5 text-rose-600 animate-pulse" />;
    } else if (sensor.nivel === 'laranja') {
      bgClass = 'bg-orange-50 text-orange-800 border-orange-300';
      icon = <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />;
    } else if (sensor.nivel === 'amarelo') {
      bgClass = 'bg-amber-50 text-amber-800 border-amber-300';
      icon = <Info className="w-3.5 h-3.5 text-amber-600" />;
    }

    if (compact) {
      return (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${bgClass}`}
          title={sensor.motivo}
        >
          {icon}
          <span>{sensor.label}</span>
        </span>
      );
    }

    return (
      <div className={`p-3.5 rounded-xl border-2 ${bgClass} transition-all`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-bold text-xs uppercase tracking-wide">{sensor.label}</span>
            {sensor.isManual && (
              <span className="bg-white/80 text-gray-700 text-[10px] px-1.5 py-0.5 rounded font-medium border border-gray-200">
                Ajuste Manual
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setPrioManualNivel(sensor.nivel);
              setPrioManualMotivo(sensor.motivo || '');
              setModalPrioAberto(true);
            }}
            className="text-[11px] font-semibold underline opacity-80 hover:opacity-100 flex items-center gap-1 shrink-0"
          >
            <Edit2 className="w-3 h-3" /> Ajustar Nível
          </button>
        </div>

        {sensor.professorNome && (
          <p className="mt-1.5 text-xs font-semibold">
            Apontado por: <span className="underline">{sensor.professorNome}</span>
          </p>
        )}

        {sensor.motivo && (
          <p className="mt-1 text-xs opacity-90 leading-relaxed font-medium">
            &ldquo;{sensor.motivo}&rdquo;
          </p>
        )}

        {sensor.detalhesOcorrencias?.length > 1 && (
          <div className="mt-2 pt-2 border-t border-black/10">
            <button
              type="button"
              onClick={() => setVerDetalhesSensor(!verDetalhesSensor)}
              className="text-xs font-medium flex items-center gap-1 opacity-90 hover:opacity-100"
            >
              {verDetalhesSensor ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {verDetalhesSensor ? 'Ocultar histórico' : `Ver todas as ${sensor.detalhesOcorrencias.length} ocorrências`}
            </button>
            {verDetalhesSensor && (
              <ul className="mt-2 space-y-1.5 text-[11px] bg-white/80 p-2 rounded-lg border border-black/5 max-h-36 overflow-y-auto">
                {sensor.detalhesOcorrencias.map((d, idx) => (
                  <li key={idx} className="flex flex-col gap-0.5 border-b border-black/5 last:border-0 pb-1">
                    <span className="font-semibold text-gray-800">
                      {d.tipo} • {d.professor || 'Educador'} ({d.data ? new Date(d.data).toLocaleDateString('pt-BR') : ''})
                    </span>
                    <span className="text-gray-600">{d.descricao}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-5 space-y-3.5 pb-20">
      {/* Topo Compacto & Otimizado para Mobile */}
      <div className="bg-white p-3.5 sm:p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-200 shrink-0">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight leading-tight">
                Feedback Day
              </h1>
              <p className="text-[11px] text-gray-500 font-medium">
                Chamada ágil e recepção de responsáveis
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
            <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <input
              type="date"
              value={dataEvento}
              onChange={(e) => setDataEvento(e.target.value)}
              className="bg-transparent text-xs font-semibold text-gray-800 focus:outline-none w-28"
            />
          </div>
        </div>

        {/* Botão de Destaque: Ver Presentes */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setModalPresentesAberto(true)}
            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-xs sm:text-sm shadow-md shadow-emerald-200 hover:brightness-105 active:scale-95 transition"
          >
            <Users className="w-4 h-4" />
            <span>Ver Presentes</span>
            <span className="bg-white/25 px-2 py-0.5 rounded-full text-xs font-black">
              {presentes.length}
            </span>
          </button>

          <Button
            variant="outline"
            onClick={exportarCSV}
            disabled={presentes.length === 0}
            className="p-2.5 rounded-xl text-gray-600 hover:text-emerald-700 shrink-0"
            title="Exportar Lista de Presentes em CSV"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
          </Button>
        </div>
      </div>

      {/* Mini Resumo de Indicadores (Compacto para Mobile) */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <div className="bg-white p-2.5 rounded-xl border border-indigo-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-indigo-600 uppercase block">Total Presentes</span>
          <span className="text-lg font-black text-indigo-950 leading-tight">
            {resumo.totalPresentes || 0}
          </span>
          <span className="text-[10px] text-gray-400 block">de {resumo.totalInscritos || 0}</span>
        </div>

        <div className="bg-white p-2.5 rounded-xl border border-rose-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-rose-600 uppercase flex items-center justify-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping" />
            Urgente
          </span>
          <span className="text-lg font-black text-rose-950 leading-tight">
            {resumo.vermelho || 0}
          </span>
          <span className="text-[10px] text-rose-600 block">chamar hoje</span>
        </div>

        <div className="bg-white p-2.5 rounded-xl border border-orange-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-orange-600 uppercase block">Alta Prioridade</span>
          <span className="text-lg font-black text-orange-950 leading-tight">
            {resumo.laranja || 0}
          </span>
          <span className="text-[10px] text-orange-600 block">reincidentes</span>
        </div>

        <div className="bg-white p-2.5 rounded-xl border border-amber-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-amber-600 uppercase block">Atenção</span>
          <span className="text-lg font-black text-amber-950 leading-tight">
            {resumo.amarelo || 0}
          </span>
          <span className="text-[10px] text-amber-600 block">alinhamento</span>
        </div>

        <div className="bg-white p-2.5 rounded-xl border border-emerald-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-emerald-600 uppercase block">Tranquilo</span>
          <span className="text-lg font-black text-emerald-950 leading-tight">
            {resumo.verde || 0}
          </span>
          <span className="text-[10px] text-emerald-600 block">ok/elogios</span>
        </div>

        <div className="bg-white p-2.5 rounded-xl border border-blue-100 shadow-sm text-center">
          <span className="text-[10px] font-bold text-blue-600 uppercase block">Atendidos</span>
          <span className="text-lg font-black text-blue-950 leading-tight">
            {resumo.totalAtendidos || 0}
          </span>
          <span className="text-[10px] text-gray-500 block">faltam {resumo.aguardandoAtendimento || 0}</span>
        </div>
      </div>

      {/* BLOCO FIXADO / CONGELADO NO TOPO AO ROLAR (STICKY SEARCH HEADER) */}
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-md -mx-3 px-3 sm:-mx-5 sm:px-5 py-2 space-y-2 border-b border-gray-200/90 shadow-xs transition-all">
        {/* FILTROS POR NÍVEL NO TOPO (Botões de Toque Rápido com Scroll Horizontal) */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
          <button
            type="button"
            onClick={() => setFiltroNivel('todos')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition active:scale-95 flex items-center gap-1.5 ${
              filtroNivel === 'todos'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span>Todos</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                filtroNivel === 'todos' ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {alunos.length}
            </span>
          </button>

          {niveisDisponiveis.map((n) => (
            <button
              key={n.chave}
              type="button"
              aria-label={`Filtrar por ${n.label}`}
              onClick={() => setFiltroNivel(n.chave)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition active:scale-95 flex items-center gap-1.5 ${
                filtroNivel === n.chave
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span>{n.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                  filtroNivel === n.chave ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {n.total}
              </span>
            </button>
          ))}
        </div>

        {/* Barra de Busca + Filtro Rápido de Status (Pendentes x Presentes) */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Buscar aluno por nome, turma ou responsável... Ex: João, Marciel, Sophia..."
              value={buscaAluno}
              onChange={handleMudarBusca}
              className="w-full pl-9 pr-8 text-xs sm:text-sm py-2 rounded-xl bg-white shadow-sm border-gray-200 font-medium"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            {buscaAluno && (
              <button
                type="button"
                onClick={() => setBuscaAluno('')}
                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200 shrink-0">
            <button
              type="button"
              onClick={() => setFiltroStatus('todos')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition ${
                filtroStatus === 'todos' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setFiltroStatus('pendentes')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition flex items-center gap-1 ${
                filtroStatus === 'pendentes' ? 'bg-amber-50 text-amber-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ⏳ Só Faltantes
            </button>
            <button
              type="button"
              onClick={() => setFiltroStatus('presentes')}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition flex items-center gap-1 ${
                filtroStatus === 'presentes' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ✓ Presentes
            </button>
          </div>
        </div>

        {/* Indicador Imediato de Busca Ativa com Resultados Logo Abaixo */}
        {buscaAluno.trim() && (
          <div className="flex items-center justify-between text-xs font-semibold text-indigo-900 bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-200">
            <span>Resultados para &ldquo;{buscaAluno}&rdquo;:</span>
            <span className="font-bold bg-white px-2 py-0.5 rounded-md shadow-xs">
              {alunosFiltrados.length} encontrado{alunosFiltrados.length === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>

      {/* LISTA PRINCIPAL DE ALUNOS (Focada no Mobile, Rápida e com Toque para Check-in) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-semibold text-gray-500 px-1">
          <span>
            Exibindo {alunosFiltrados.length} aluno{alunosFiltrados.length === 1 ? '' : 's'}
          </span>
          <span className="text-[11px] text-gray-400">Toque no aluno para registrar presença</span>
        </div>

        {carregandoAlunos ? (
          <div className="bg-white p-10 rounded-2xl border border-gray-100 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            <span className="text-xs font-medium">Carregando lista de alunos...</span>
          </div>
        ) : alunosFiltrados.length === 0 ? (
          <div className="bg-white p-10 rounded-2xl border border-dashed border-gray-200 text-center text-gray-400 text-xs">
            Nenhum aluno encontrado com os filtros selecionados.
          </div>
        ) : (
          <div className="space-y-1.5">
            {alunosFiltrados.map((aluno) => {
              const sensor = aluno.sensorPrioridade;
              const isPresente = aluno.presente;

              return (
                <div
                  key={aluno.id}
                  onClick={() => handleAbrirCheckin(aluno)}
                  role="button"
                  tabIndex={0}
                  className={`w-full text-left p-3 sm:p-3.5 rounded-2xl border transition active:scale-[0.99] cursor-pointer flex items-center justify-between gap-3 shadow-sm ${
                    isPresente
                      ? 'bg-emerald-50/60 border-emerald-200 hover:bg-emerald-50'
                      : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-md'
                  }`}
                >
                  {/* Lado Esquerdo: Tag de Nível + Nome Completo Sem Truncar */}
                  <div className="flex-1 min-w-0 pr-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 shrink-0">
                        {aluno.nivel || (aluno.turma ? `Turma ${aluno.turma}` : 'Sem Nível')}
                      </span>
                      {isPresente && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md border border-emerald-300 shrink-0">
                          <Check className="w-3 h-3 text-emerald-700" />
                          Presente
                        </span>
                      )}
                    </div>

                    {/* Nome do aluno 100% completo, sem ... e com quebra suave de linha */}
                    <h3 className="font-bold text-sm sm:text-base text-gray-900 leading-snug break-words">
                      {aluno.nome}
                    </h3>
                  </div>

                  {/* Lado Direito: Sensor de Prioridade + Ação de Check-in */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {renderSensorBadge(sensor, true)}

                    {!isPresente ? (
                      <span className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-0.5">
                        Check-in <span className="text-sm leading-none">→</span>
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold text-gray-500">
                        {aluno.presenca?.presenca_horario
                          ? new Date(aluno.presenca.presenca_horario).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'Registrado'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* POPUP DE CHECK-IN DO ALUNO SELECIONADO (Modal Touch-Friendly) */}
      <Modal
        isOpen={modalCheckinAberto}
        onClose={() => setModalCheckinAberto(false)}
        title={alunoSelecionado ? `Check-in: ${alunoSelecionado.nome}` : 'Check-in'}
        maxWidth="lg"
      >
        {alunoSelecionado && (
          <div className="space-y-4">
            {/* Cabeçalho do Aluno no Modal */}
            <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider block">
                Aluno Selecionado
              </span>
              <h3 className="text-base font-black text-gray-900 leading-tight mt-0.5">
                {alunoSelecionado.nome}
              </h3>
              <p className="text-xs text-gray-600 font-medium mt-0.5">
                {alunoSelecionado.nivel || (alunoSelecionado.turma ? `Turma ${alunoSelecionado.turma}` : '')}
                {alunoSelecionado.turno && ` • Turno: ${alunoSelecionado.turno}`}
                {alunoSelecionado.responsavel_nome && ` • Resp. Cadastrado: ${alunoSelecionado.responsavel_nome}`}
              </p>
            </div>

            {/* SENSOR DE PRIORIDADE VISÍVEL EM DESTAQUE */}
            {renderSensorBadge(alunoSelecionado.sensorPrioridade)}

            {/* Formulário de Registro do Responsável */}
            <form onSubmit={handleSalvarPresenca} className="space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                  Quem compareceu ao Feedback Day? *
                </label>

                {/* Sugestões com 1 clique (para agilizar na fila) */}
                {alunoSelecionado.sugestoesResponsaveis?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                    <span className="text-[11px] text-gray-500 font-medium">Opções rápidas:</span>
                    {alunoSelecionado.sugestoesResponsaveis.map((s, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setResponsavelPresente(s.nome);
                          setParentesco(s.parentesco);
                        }}
                        className="px-2.5 py-1 text-xs rounded-lg font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-200 active:scale-95 transition"
                      >
                        👤 {s.nome} ({s.parentesco})
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-2">
                    <Input
                      type="text"
                      placeholder="Nome do responsável presente..."
                      value={responsavelPresente}
                      onChange={(e) => setResponsavelPresente(e.target.value)}
                      required
                      className="text-xs sm:text-sm font-semibold py-2"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      placeholder="Parentesco (Mãe, Pai...)"
                      value={parentesco}
                      onChange={(e) => setParentesco(e.target.value)}
                      className="text-xs sm:text-sm py-2"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">
                  Observações da Recepção / Coordenação (Opcional)
                </label>
                <textarea
                  rows={2}
                  placeholder="Ex: Mãe pediu para conversar com o professor de matemática; veio com o irmão..."
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  className="w-full text-xs p-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-medium"
                />
              </div>

              {/* Botões de Ação do Check-in */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2">
                {alunoSelecionado.presente ? (
                  <>
                    <Button
                      type="submit"
                      disabled={mutacaoPresenca.isPending}
                      className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2.5"
                    >
                      {mutacaoPresenca.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atualizar Dados'}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => handleConfirmarRemocao(alunoSelecionado.presenca?.id, alunoSelecionado.nome)}
                      className="w-full sm:w-auto text-xs py-2.5"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remover Presença
                    </Button>
                  </>
                ) : (
                  <Button
                    type="submit"
                    disabled={mutacaoPresenca.isPending}
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm shadow-md shadow-emerald-200 active:scale-95 transition"
                  >
                    {mutacaoPresenca.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      '✓ CONFIRMAR PRESENÇA NO FEEDBACK DAY'
                    )}
                  </Button>
                )}
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* MODAL COM A LISTA DE PRESENTES NO EVENTO */}
      <Modal
        isOpen={modalPresentesAberto}
        onClose={() => setModalPresentesAberto(false)}
        title={`Presentes no Feedback Day (${presentes.length})`}
        maxWidth="xl"
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="Filtrar presentes..."
                value={buscaPresentes}
                onChange={(e) => setBuscaPresentes(e.target.value)}
                className="text-xs py-1.5 pl-8 w-full"
              />
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
            </div>

            <div className="flex flex-wrap items-center gap-1 text-[11px] font-semibold bg-gray-50 p-1 rounded-xl border border-gray-200">
              <button
                type="button"
                onClick={() => setFiltroPresentes('todos')}
                className={`px-2.5 py-1 rounded-lg transition ${
                  filtroPresentes === 'todos' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                Todos ({presentes.length})
              </button>
              <button
                type="button"
                onClick={() => setFiltroPresentes('urgentes')}
                className={`px-2.5 py-1 rounded-lg transition ${
                  filtroPresentes === 'urgentes' ? 'bg-rose-50 text-rose-800 shadow-sm' : 'text-rose-600'
                }`}
              >
                🔴 Urgentes ({resumo.vermelho + resumo.laranja || 0})
              </button>
              <button
                type="button"
                onClick={() => setFiltroPresentes('aguardando')}
                className={`px-2.5 py-1 rounded-lg transition ${
                  filtroPresentes === 'aguardando' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                Aguardando ({resumo.aguardandoAtendimento || 0})
              </button>
              <button
                type="button"
                onClick={() => setFiltroPresentes('atendidos')}
                className={`px-2.5 py-1 rounded-lg transition ${
                  filtroPresentes === 'atendidos' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                Atendidos ({resumo.totalAtendidos || 0})
              </button>
            </div>
          </div>

          {presentesFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-xs">
              Nenhum participante encontrado com os filtros selecionados.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto pr-1 space-y-2">
              {presentesFiltrados.map((a) => {
                const p = a.presenca || {};
                const s = a.sensorPrioridade || {};
                const hora = p.presenca_horario
                  ? new Date(p.presenca_horario).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                  : '--:--';

                return (
                  <div
                    key={a.id}
                    className={`p-3 rounded-xl border transition ${
                      s.nivel === 'vermelho' && !p.atendido
                        ? 'bg-rose-50/50 border-rose-200'
                        : 'bg-white border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs sm:text-sm text-gray-900">{a.nome}</span>
                          <span className="text-[10px] text-gray-500 font-semibold bg-gray-100 px-1.5 py-0.2 rounded">
                            {hora}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {a.nivel || (a.turma ? `Turma ${a.turma}` : '')} • {a.turno}
                        </p>
                        <p className="text-xs font-semibold text-gray-800 mt-1">
                          👤 Veio com:{' '}
                          <span className="font-bold text-indigo-900">
                            {p.responsavel_presente || 'Responsável'}
                          </span>{' '}
                          {p.parentesco && <span className="text-gray-500">({p.parentesco})</span>}
                        </p>
                        {p.observacao && (
                          <p className="text-[11px] text-gray-600 bg-gray-50 p-1.5 rounded mt-1 font-medium">
                            Obs: {p.observacao}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        {renderSensorBadge(s, true)}

                        <button
                          type="button"
                          onClick={() =>
                            mutacaoAtendido.mutate({ presencaId: p.id, atendido: !p.atendido })
                          }
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition ${
                            p.atendido
                              ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                              : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          {p.atendido ? <Check className="w-3.5 h-3.5 text-blue-600" /> : <Clock className="w-3.5 h-3.5" />}
                          {p.atendido ? 'Atendido' : 'Aguardando'}
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                      {s.professorNome ? (
                        <span className="text-[10px] text-gray-500">
                          Apontado por: <span className="font-semibold text-gray-700">{s.professorNome}</span>
                        </span>
                      ) : <span />}

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setModalPresentesAberto(false);
                            handleAbrirCheckin(a);
                          }}
                          className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConfirmarRemocao(p.id, a.nome)}
                          className="text-xs font-semibold text-rose-600 hover:underline flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Desfazer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-2 flex justify-between items-center border-t border-gray-100">
            <Button
              variant="outline"
              onClick={exportarCSV}
              disabled={presentes.length === 0}
              className="text-xs flex items-center gap-1.5"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Exportar CSV
            </Button>
            <Button variant="outline" onClick={() => setModalPresentesAberto(false)} className="text-xs">
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL PARA AJUSTAR PRIORIDADE MANUALMENTE */}
      <Modal
        isOpen={modalPrioAberto}
        onClose={() => setModalPrioAberto(false)}
        title="Ajustar Prioridade do Aluno"
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Defina o nível de prioridade para a coordenação conversar com os responsáveis de{' '}
            <span className="font-bold text-gray-800">{alunoSelecionado?.nome}</span>.
          </p>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              Nível de Prioridade
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { valor: 'vermelho', label: '🔴 Urgente (Chamar Hoje)', bg: 'bg-rose-50 text-rose-800 border-rose-300' },
                { valor: 'laranja', label: '🟠 Alta Prioridade', bg: 'bg-orange-50 text-orange-800 border-orange-300' },
                { valor: 'amarelo', label: '🟡 Atenção', bg: 'bg-amber-50 text-amber-800 border-amber-300' },
                { valor: 'verde', label: '🟢 Normal / Elogio', bg: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
              ].map((opt) => (
                <button
                  key={opt.valor}
                  type="button"
                  onClick={() => setPrioManualNivel(opt.valor)}
                  className={`p-2.5 text-xs font-bold rounded-xl border text-left transition ${
                    prioManualNivel === opt.valor
                      ? opt.bg + ' ring-2 ring-offset-1 ring-gray-400'
                      : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              Motivo do Ajuste / Recado para a Coordenação
            </label>
            <textarea
              rows={3}
              value={prioManualMotivo}
              onChange={(e) => setPrioManualMotivo(e.target.value)}
              placeholder="Ex: Aluno precisa conversar sobre rendimento no contraturno; mãe solicitou atendimento com urgência..."
              className="w-full text-xs p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setModalPrioAberto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                mutacaoPrioridade.mutate({
                  id_aluno: alunoSelecionado.id,
                  data_evento: dataEvento,
                  prioridade: prioManualNivel,
                  motivo: prioManualMotivo
                });
              }}
              disabled={mutacaoPrioridade.isPending}
              className="bg-indigo-600 text-white font-bold"
            >
              {mutacaoPrioridade.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Prioridade'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
